var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useImmer } from 'use-immer';
import { FaPlus, FaTrash, FaTimes, FaSearch, FaFileImport, FaDownload } from 'react-icons/fa';
import { ImageSelectModal } from '../ImageSelectModal/ImageSelectModal';
import './UxPanelEditor.scss';
import { createUxPanel, deleteUxPanel, getArchivedUxPanels, getUxPanels, restoreUxPanel, updateUxPanel } from '../../api/ux-panels';
import { backendUrl } from '@common/contracts';
import { getUxPanelImages } from '../../api/ux-panel-images';
import { EventBus } from '../../utils/EventBus';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
// Normalizes the image URL by prepending backendUrl if it is not a full URL
const normalizeImageUrl = (url) => {
    if (!url)
        return undefined;
    return url.startsWith('http') ? url : `${backendUrl}${url}`;
};
// Panel type display labels
const PANEL_TYPE_LABELS = {
    'fullscreen': 'Full Screen',
    'modal': 'Modal'
};
export const UxPanelEditor = ({ levelId, readOnly = false, onPanelsMutated, onUnsavedChangesChange, onRegisterSave }) => {
    // List of all UX panels for the current level
    const [panels, setPanels] = useImmer([]);
    // Soft-deleted panels, shown in the restorable Archive section below the grid.
    const [archivedPanels, setArchivedPanels] = useImmer([]);
    const [restoringId, setRestoringId] = useState(null);
    // Currently edited panel
    const [currentPanel, setCurrentPanel] = useImmer(null);
    // UI state
    const [isEditing, setIsEditing] = useState(false);
    const [selectedPanelId, setSelectedPanelId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    // Search and filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPanelTypes, setSelectedPanelTypes] = useState([]);
    const [filteredPanels, setFilteredPanels] = useImmer([]);
    // State for the card context menu
    const [openDropdownId, setOpenDropdownId] = useState(null);
    // Ref for file uploads
    const fileInputRef = useRef(null);
    // State for UX panel images and selection modals
    const [uxPanelImages, setUxPanelImages] = useImmer([]);
    const [showBackgroundModal, setShowBackgroundModal] = useState(false);
    const [showCenterModal, setShowCenterModal] = useState(false);
    // Load UX panel images
    useEffect(() => {
        const fetchUxPanelImages = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const images = yield getUxPanelImages(levelId);
                setUxPanelImages(images.filter(img => !img.isArchived));
            }
            catch (error) {
                console.error('Error loading UX panel images:', error);
            }
        });
        fetchUxPanelImages();
    }, [levelId, setUxPanelImages]);
    // Checks whether the panel image is in use
    const isUxPanelImageUsed = useCallback((imageUrl) => {
        // Check usage as a background image
        const usedAsBackground = panels.some(panel => {
            return normalizeImageUrl(panel.backgroundImage) === normalizeImageUrl(imageUrl);
        });
        if (usedAsBackground)
            return true;
        // Check usage as a center image
        const usedAsCenter = panels.some(panel => {
            return normalizeImageUrl(panel.centerImage) === normalizeImageUrl(imageUrl);
        });
        return usedAsCenter;
    }, [panels]);
    // State for the delete confirmation modal
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteWarningMessage] = useState('');
    const [pendingDeletePanelId] = useState(null);
    // State for the save confirmation modal
    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
    const [saveWarningMessage] = useState('');
    const [, setPendingPanelToSave] = useState(null);
    // --- Unsaved changes tracking ---
    const originalPanelRef = useRef(null);
    // Save the initial snapshot when editing starts
    useEffect(() => {
        if (isEditing && currentPanel) {
            originalPanelRef.current = JSON.parse(JSON.stringify(currentPanel));
        }
        else {
            originalPanelRef.current = null;
        }
    }, [isEditing]);
    const hasUnsavedChanges = useMemo(() => {
        if (!isEditing || !currentPanel || !originalPanelRef.current)
            return false;
        return JSON.stringify(currentPanel) !== JSON.stringify(originalPanelRef.current);
    }, [isEditing, currentPanel]);
    // Load panels on initialization
    useEffect(() => {
        const fetchPanels = () => __awaiter(void 0, void 0, void 0, function* () {
            setIsLoading(true);
            setError(null);
            try {
                const panelsData = yield getUxPanels(levelId);
                setPanels(panelsData);
            }
            catch (err) {
                console.error('Error fetching UX panels:', err);
                setError(`Failed to load UX panels: ${err.message}`);
            }
            finally {
                setIsLoading(false);
            }
        });
        fetchPanels();
        getArchivedUxPanels(levelId)
            .then(list => setArchivedPanels(list))
            .catch(err => console.error('Failed to load archived UX panels', err));
    }, [levelId, setPanels, setArchivedPanels]);
    // Close the dropdown when clicking outside the card
    useEffect(() => {
        const handleClickOutside = () => {
            setOpenDropdownId(null);
        };
        if (openDropdownId) {
            document.addEventListener('click', handleClickOutside);
        }
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [openDropdownId]);
    // Effect for filtering panels
    useEffect(() => {
        setFilteredPanels(panels.filter(panel => {
            // Filter by title
            const titleMatches = searchTerm ?
                panel.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    panel.text.toLowerCase().includes(searchTerm.toLowerCase()) : true;
            // Filter by panel type
            const typeMatches = selectedPanelTypes.length > 0 ?
                selectedPanelTypes.includes(panel.panelType) : true;
            return titleMatches && typeMatches;
        }));
    }, [panels, searchTerm, selectedPanelTypes, setFilteredPanels]);
    // Handler for creating a new panel
    const handleCreatePanel = () => {
        const newPanel = {
            id: `ux_panel_${uuidv4()}`,
            levelId,
            title: 'New UX Panel',
            text: 'Panel description',
            panelType: 'fullscreen',
            isArchived: false
        };
        setCurrentPanel(newPanel);
        setIsEditing(true);
        setSelectedPanelId(null);
        setError(null);
    };
    // Handler for editing an existing panel
    const handleEditPanel = (panelId) => {
        const panelToEdit = panels.find(p => p.id === panelId);
        if (panelToEdit) {
            setCurrentPanel(panelToEdit);
            setIsEditing(true);
            setSelectedPanelId(panelId);
            setError(null);
        }
    };
    // Direct panel deletion (called after confirmation)
    const handleDeletePanel = (panelId_1, ...args_1) => __awaiter(void 0, [panelId_1, ...args_1], void 0, function* (panelId, skipBasicConfirm = false) {
        if (skipBasicConfirm || confirm('Are you sure you want to delete this UX panel?')) {
            setIsLoading(true);
            setError(null);
            try {
                yield deleteUxPanel(levelId, panelId);
                const archived = panels.find(p => p.id === panelId);
                setPanels(draft => draft.filter(p => p.id !== panelId));
                if (archived)
                    setArchivedPanels(draft => { draft.unshift(Object.assign(Object.assign({}, archived), { isArchived: true })); });
                // The server also stripped the panel's show_ux_panel actions from
                // cell outcomes — let the parent reload its map state.
                onPanelsMutated === null || onPanelsMutated === void 0 ? void 0 : onPanelsMutated();
                if (selectedPanelId === panelId) {
                    setSelectedPanelId(null);
                    setCurrentPanel(null);
                    setIsEditing(false);
                }
                // Notify other components that the panel has been deleted
                EventBus.emit('uxPanelDeleted', panelId);
            }
            catch (err) {
                console.error('Error deleting UX panel:', err);
                setError(`Failed to delete UX panel: ${err.message}`);
            }
            finally {
                setIsLoading(false);
            }
        }
    });
    // Bring an archived panel back into the active set. It returns UNATTACHED —
    // deleting stripped its show_ux_panel actions — re-attach from the events.
    const handleRestorePanel = (panelId) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        setRestoringId(panelId);
        setError(null);
        try {
            yield restoreUxPanel(levelId, panelId);
            const restored = archivedPanels.find(p => p.id === panelId);
            setArchivedPanels(draft => draft.filter(p => p.id !== panelId));
            if (restored)
                setPanels(draft => { draft.push(Object.assign(Object.assign({}, restored), { isArchived: false })); });
            onPanelsMutated === null || onPanelsMutated === void 0 ? void 0 : onPanelsMutated();
        }
        catch (err) {
            setError(`Failed to restore UX panel: ${(_c = (_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) !== null && _c !== void 0 ? _c : err.message}`);
        }
        finally {
            setRestoringId(null);
        }
    });
    // Handler for saving panel changes
    const handleSavePanel = () => {
        if (!currentPanel)
            return;
        // Validate required fields
        if (!currentPanel.title || !currentPanel.text) {
            setError('Please fill in all required fields (title and text).');
            return;
        }
        const savePanel = (panelToSave) => __awaiter(void 0, void 0, void 0, function* () {
            setIsSaving(true);
            setError(null);
            try {
                let savedPanel;
                if (selectedPanelId) {
                    // Update the existing panel - pass only the mutable fields
                    const updateData = {
                        title: panelToSave.title,
                        text: panelToSave.text,
                        panelType: panelToSave.panelType,
                        backgroundImage: panelToSave.backgroundImage,
                        centerImage: panelToSave.centerImage,
                        isArchived: panelToSave.isArchived
                    };
                    savedPanel = yield updateUxPanel(levelId, selectedPanelId, updateData);
                }
                else {
                    // Create a new panel - pass only the required fields
                    const createData = {
                        levelId: levelId,
                        title: panelToSave.title,
                        text: panelToSave.text,
                        panelType: panelToSave.panelType,
                        backgroundImage: panelToSave.backgroundImage,
                        centerImage: panelToSave.centerImage
                    };
                    savedPanel = yield createUxPanel(levelId, createData);
                }
                // Update the panel list
                setPanels(draft => {
                    const index = draft.findIndex(p => p.id === savedPanel.id);
                    if (index >= 0) {
                        draft[index] = savedPanel;
                    }
                    else {
                        draft.push(savedPanel);
                    }
                });
                // Notify other components
                EventBus.emit('uxPanelUpdated', savedPanel);
                // Close the editor
                setIsEditing(false);
                setSelectedPanelId(null);
                setCurrentPanel(null);
            }
            catch (err) {
                console.error('Error saving UX panel:', err);
                setError(`Failed to save UX panel: ${err.message}`);
            }
            finally {
                setIsSaving(false);
            }
        });
        // Save the panel
        savePanel(currentPanel);
    };
    const discardEditing = () => {
        setIsEditing(false);
        setCurrentPanel(null);
        setError(null);
    };
    const handleCancelEdit = () => {
        discardEditing();
    };
    // Notify the parent about unsaved changes
    useEffect(() => {
        if (onUnsavedChangesChange) {
            onUnsavedChangesChange(hasUnsavedChanges, (currentPanel === null || currentPanel === void 0 ? void 0 : currentPanel.title) || '');
        }
    }, [hasUnsavedChanges, currentPanel === null || currentPanel === void 0 ? void 0 : currentPanel.title]);
    // Register the save function
    useEffect(() => {
        if (onRegisterSave) {
            onRegisterSave(handleSavePanel);
        }
    }, [onRegisterSave, handleSavePanel]);
    // Handlers for panel field changes
    const handlePanelFieldChange = (field, value) => {
        setCurrentPanel(draft => {
            if (!draft)
                return;
            draft[field] = value;
        });
    };
    // Handler for clearing filters
    const handleClearFilters = () => {
        setSearchTerm('');
        setSelectedPanelTypes([]);
    };
    // Handler for toggling panel type selection
    const handlePanelTypeToggle = (panelType) => {
        setSelectedPanelTypes(prev => {
            if (prev.includes(panelType)) {
                return prev.filter(t => t !== panelType);
            }
            else {
                return [...prev, panelType];
            }
        });
    };
    // Handler for importing CSV
    const handleImportCSV = () => {
        var _a;
        (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click();
    };
    // Handler for file selection
    const handleFileSelect = (e) => __awaiter(void 0, void 0, void 0, function* () {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
                alert('Please select a CSV file');
                return;
            }
            try {
                const content = yield file.text();
                const lines = content.split('\n');
                const headers = lines[0].split(',');
                // Simple example of CSV parsing
                const newPanels = [];
                for (let i = 1; i < lines.length; i++) {
                    if (!lines[i].trim())
                        continue;
                    const values = lines[i].split(',');
                    const panel = {
                        id: `ux_panel_${uuidv4()}`,
                        levelId,
                        title: values[headers.indexOf('title')] || 'Untitled Panel',
                        text: values[headers.indexOf('text')] || 'No description',
                        panelType: (values[headers.indexOf('panelType')] || 'custom'),
                        isArchived: false
                    };
                    newPanels.push(panel);
                }
                // Create the new panels
                for (const panel of newPanels) {
                    try {
                        yield createUxPanel(levelId, panel);
                    }
                    catch (err) {
                        console.error('Error creating panel from CSV:', err);
                    }
                }
                // Reload the panel list
                const updatedPanels = yield getUxPanels(levelId);
                setPanels(updatedPanels);
            }
            catch (err) {
                console.error('Error processing CSV file:', err);
                alert('Error processing CSV file');
            }
        }
    });
    // Handler for exporting CSV
    const handleExportCSV = () => {
        const csvContent = [
            ['title', 'text', 'panelType', 'backgroundImage', 'centerImage'].join(','),
            ...panels.map(panel => [
                panel.title,
                panel.text,
                panel.panelType,
                panel.backgroundImage || '',
                panel.centerImage || ''
            ].join(','))
        ].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ux-panels-level-${levelId}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };
    // Render the panel list
    const renderPanelsList = () => (_jsxs("div", { className: "ux-panels-list", children: [_jsx("div", { className: "ux-panels-toolbar", children: _jsx("div", { className: "width-container", children: _jsxs("div", { className: "toolbar-content", children: [_jsxs("div", { className: "search-container", children: [_jsx(FaSearch, { className: "search-icon", size: 14 }), _jsx("input", { type: "text", placeholder: "Search by title...", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), className: "search-input" }), searchTerm && (_jsx("button", { className: "clear-search-button", onClick: () => setSearchTerm(''), title: "Clear search", children: _jsx(FaTimes, { size: 12 }) }))] }), _jsxs("div", { className: "filters-actions-row", children: [_jsxs("div", { className: "filters-container", children: [_jsx("div", { className: "type-filter", children: Object.entries(PANEL_TYPE_LABELS).map(([type, label]) => (_jsx("button", { className: `type-filter-button ${type}-button ${selectedPanelTypes.includes(type) ? 'active' : ''}`, onClick: () => handlePanelTypeToggle(type), title: `Filter by ${label}`, children: label }, type))) }), (searchTerm || selectedPanelTypes.length > 0) && (_jsxs("button", { className: "clear-filters-button", onClick: handleClearFilters, title: "Clear all filters", children: [_jsx(FaTimes, { size: 10 }), " Clear"] }))] }), _jsxs("div", { className: "import-export-container", children: [!readOnly && (_jsxs("button", { className: "import-button", onClick: handleImportCSV, title: "Import panels from CSV", disabled: isLoading, children: [_jsx(FaFileImport, { size: 11 }), " Import"] })), _jsx("input", { type: "file", ref: fileInputRef, style: { display: 'none' }, accept: ".csv", onChange: handleFileSelect }), _jsxs("button", { className: "export-button", onClick: handleExportCSV, title: "Export panels to CSV", disabled: isLoading, children: [_jsx(FaDownload, { size: 11 }), " Export"] })] })] })] }) }) }), _jsxs("div", { className: "ux-panels-content", children: [_jsxs("div", { className: "ux-panels-header", children: [_jsx("h2", { className: "section-title", children: "UX Panels" }), !readOnly && (_jsxs("button", { className: "add-button", onClick: handleCreatePanel, title: "Create new UX panel", disabled: isLoading, children: [_jsx(FaPlus, {}), " Add UX Panel"] }))] }), error && _jsx("div", { className: "error-message", children: error }), isLoading ? (_jsx("div", { className: "loading-state", children: "Loading UX panels..." })) : filteredPanels.length === 0 ? (_jsx("div", { className: "empty-state", children: panels.length === 0 ? (_jsxs(_Fragment, { children: [_jsxs("p", { children: ["No UX panels yet.", readOnly ? '' : ' Create your first panel!'] }), !readOnly && (_jsxs("button", { onClick: handleCreatePanel, className: "primary-button", children: [_jsx(FaPlus, {}), " Create Panel"] }))] })) : (_jsx("p", { children: "No panels match your search criteria." })) })) : (_jsx("div", { className: "ux-panels-grid", children: filteredPanels.map(panel => (_jsxs("div", { className: `ux-panel-card ${selectedPanelId === panel.id ? 'selected' : ''}${readOnly ? ' read-only' : ''}`, onClick: readOnly ? undefined : () => handleEditPanel(panel.id), children: [panel.panelType === 'modal' ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "ux-panel-card-screen-background" }), _jsx("div", { className: `ux-panel-card-type-badge ${panel.panelType}-badge`, children: PANEL_TYPE_LABELS[panel.panelType] }), _jsxs("div", { className: "ux-panel-card-modal-window", children: [_jsx("div", { className: "ux-panel-card-modal-background", style: {
                                                        backgroundImage: panel.backgroundImage
                                                            ? `url(${normalizeImageUrl(panel.backgroundImage)})`
                                                            : undefined
                                                    } }), _jsx("div", { className: "ux-panel-card-modal-close" }), _jsxs("div", { className: "ux-panel-card-content", children: [panel.centerImage && (_jsx("div", { className: "ux-panel-card-center-image", children: _jsx("img", { src: normalizeImageUrl(panel.centerImage), alt: "Center image" }) })), _jsx("h3", { className: "ux-panel-card-title", children: panel.title }), _jsx("p", { className: "ux-panel-card-text", children: panel.text })] })] })] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "ux-panel-card-background", style: {
                                                backgroundImage: panel.backgroundImage
                                                    ? `url(${normalizeImageUrl(panel.backgroundImage)})`
                                                    : undefined
                                            } }), _jsx("div", { className: `ux-panel-card-type-badge ${panel.panelType}-badge`, children: PANEL_TYPE_LABELS[panel.panelType] }), _jsxs("div", { className: "ux-panel-card-content", children: [panel.centerImage && (_jsx("div", { className: "ux-panel-card-center-image", children: _jsx("img", { src: normalizeImageUrl(panel.centerImage), alt: "Center image" }) })), _jsx("h3", { className: "ux-panel-card-title", children: panel.title }), _jsx("p", { className: "ux-panel-card-text", children: panel.text })] })] })), !readOnly && (_jsx("button", { className: "delete-btn", onClick: (e) => {
                                        e.stopPropagation();
                                        handleDeletePanel(panel.id);
                                    }, disabled: isLoading, title: "Delete", children: _jsx(FaTrash, { size: 12 }) }))] }, panel.id))) })), archivedPanels.length > 0 && (_jsxs("div", { className: "panels-archive", children: [_jsxs("h3", { className: "archive-title", children: ["Archive (", archivedPanels.length, ")"] }), _jsx("div", { className: "archive-list", children: archivedPanels.map(p => (_jsxs("div", { className: "archive-row", children: [_jsx("span", { className: "archive-name", children: p.title }), !readOnly && (_jsx("button", { className: "restore-button", onClick: () => handleRestorePanel(p.id), disabled: restoringId === p.id, children: restoringId === p.id ? 'Restoring…' : 'Restore' }))] }, p.id))) })] }))] })] }));
    // Render the phone preview
    const renderPhonePreview = () => {
        if (!currentPanel)
            return null;
        const isModal = currentPanel.panelType === 'modal';
        return (_jsx("div", { className: "phone-preview-container", children: _jsx("div", { className: "phone-frame", children: _jsx("div", { className: "phone-screen", children: isModal ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "phone-screen-background" }), _jsxs("div", { className: "phone-modal-window", children: [_jsx("div", { className: "phone-modal-background", style: {
                                            backgroundImage: currentPanel.backgroundImage
                                                ? `url(${normalizeImageUrl(currentPanel.backgroundImage)})`
                                                : undefined
                                        } }), _jsx("div", { className: "phone-modal-close" }), _jsxs("div", { className: "phone-modal-content", children: [currentPanel.centerImage && (_jsx("div", { className: "phone-center-image", children: _jsx("img", { src: normalizeImageUrl(currentPanel.centerImage), alt: "Center image" }) })), _jsx("h3", { className: "phone-title", children: currentPanel.title }), _jsx("p", { className: "phone-text", children: currentPanel.text })] })] })] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "phone-background", style: {
                                    backgroundImage: currentPanel.backgroundImage
                                        ? `url(${normalizeImageUrl(currentPanel.backgroundImage)})`
                                        : undefined
                                } }), _jsxs("div", { className: "phone-modal-content", children: [currentPanel.centerImage && (_jsx("div", { className: "phone-center-image", children: _jsx("img", { src: normalizeImageUrl(currentPanel.centerImage), alt: "Center image" }) })), _jsx("h3", { className: "phone-title", children: currentPanel.title }), _jsx("p", { className: "phone-text", children: currentPanel.text })] })] })) }) }) }));
    };
    // Render the panel editing form
    const renderPanelEditor = () => {
        if (!currentPanel)
            return null;
        return (_jsxs("div", { className: "panel-editor", children: [_jsxs("div", { className: "editor-header", children: [_jsx("h2", { children: selectedPanelId ? 'Edit UX Panel' : 'Create New UX Panel' }), _jsxs("div", { className: "editor-actions", children: [_jsxs("button", { className: "cancel-button", onClick: handleCancelEdit, disabled: isSaving, children: [_jsx(FaTimes, {}), " Cancel"] }), _jsx("button", { className: "save-button", onClick: handleSavePanel, disabled: isSaving, children: isSaving ? 'Saving...' : 'Save Panel' })] })] }), error && _jsx("div", { className: "error-message", children: error }), _jsxs("div", { className: "editor-content", children: [_jsx("div", { className: "editor-form-column", children: _jsxs("div", { className: "editor-form", children: [_jsxs("div", { className: "form-section", children: [_jsx("h3", { className: "section-title", children: "Basic Information" }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "panelTitle", children: "Title:" }), _jsx("input", { type: "text", id: "panelTitle", value: currentPanel.title, onChange: (e) => handlePanelFieldChange('title', e.target.value), placeholder: "Enter panel title", disabled: isSaving })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "panelText", children: "Text:" }), _jsx("textarea", { id: "panelText", value: currentPanel.text, onChange: (e) => handlePanelFieldChange('text', e.target.value), placeholder: "Enter panel description", rows: 4, disabled: isSaving })] })] }), _jsxs("div", { className: "form-section", children: [_jsx("h3", { className: "section-title", children: "Images" }), _jsxs("div", { className: "image-upload-group", children: [_jsxs("div", { className: "background-selector-container", children: [_jsx("label", { children: "UX panel backdrop:" }), _jsx("div", { className: "full-background-preview", children: currentPanel.backgroundImage ? (_jsxs("div", { className: "image-preview-container", children: [_jsx("img", { src: normalizeImageUrl(currentPanel.backgroundImage), alt: "Background image", className: "preview-image", onClick: () => setShowBackgroundModal(true) }), _jsxs("div", { className: "image-overlay", children: [_jsx("span", { className: "image-name", children: "UX panel backdrop" }), _jsx("button", { type: "button", className: "change-image-button", onClick: () => setShowBackgroundModal(true), children: "Change Image" })] })] })) : (_jsxs("div", { className: "no-image-placeholder", children: [_jsx("span", { children: "No image selected" }), _jsx("button", { type: "button", className: "select-image-button", onClick: () => setShowBackgroundModal(true), children: "Select Image" })] })) })] }), _jsxs("div", { className: "center-selector-container", children: [_jsx("label", { children: "UX panel art:" }), _jsx("div", { className: "full-background-preview", children: currentPanel.centerImage ? (_jsxs("div", { className: "image-preview-container", children: [_jsx("img", { src: normalizeImageUrl(currentPanel.centerImage), alt: "Center image", className: "preview-image", onClick: () => setShowCenterModal(true) }), _jsxs("div", { className: "image-overlay", children: [_jsx("span", { className: "image-name", children: "UX panel art" }), _jsx("button", { type: "button", className: "change-image-button", onClick: () => setShowCenterModal(true), children: "Change Image" })] })] })) : (_jsxs("div", { className: "no-image-placeholder", children: [_jsx("span", { children: "No image selected" }), _jsx("button", { type: "button", className: "select-image-button", onClick: () => setShowCenterModal(true), children: "Select Image" })] })) })] })] })] })] }) }), _jsx("div", { className: "editor-preview-column", children: renderPhonePreview() })] })] }));
    };
    return (_jsxs("div", { className: "ux-panel-editor-container", children: [isEditing ? renderPanelEditor() : renderPanelsList(), _jsx(ConfirmationModal, { isOpen: isDeleteModalOpen, onClose: () => setIsDeleteModalOpen(false), onConfirm: () => {
                    if (pendingDeletePanelId) {
                        handleDeletePanel(pendingDeletePanelId, true);
                    }
                    setIsDeleteModalOpen(false);
                }, title: "Delete UX Panel", message: deleteWarningMessage, confirmText: "Delete", cancelText: "Cancel", isLoading: isLoading }), _jsx(ConfirmationModal, { isOpen: isSaveModalOpen, onClose: () => { setIsSaveModalOpen(false); setPendingPanelToSave(null); }, onConfirm: () => {
                    setIsSaveModalOpen(false);
                    setPendingPanelToSave(null);
                }, title: "Save UX Panel", message: saveWarningMessage, confirmText: "Save", cancelText: "Cancel", isLoading: isSaving }), _jsx(ImageSelectModal, { isOpen: showBackgroundModal, onClose: () => setShowBackgroundModal(false), onSelectImage: (imageUrl) => {
                    // Strip backendUrl from the URL before saving
                    const relativePath = imageUrl.startsWith(backendUrl)
                        ? imageUrl.substring(backendUrl.length)
                        : imageUrl;
                    handlePanelFieldChange('backgroundImage', relativePath);
                }, images: uxPanelImages, title: "Select the UX panel backdrop", isImageUsed: isUxPanelImageUsed }), _jsx(ImageSelectModal, { isOpen: showCenterModal, onClose: () => setShowCenterModal(false), onSelectImage: (imageUrl) => {
                    // Strip backendUrl from the URL before saving
                    const relativePath = imageUrl.startsWith(backendUrl)
                        ? imageUrl.substring(backendUrl.length)
                        : imageUrl;
                    handlePanelFieldChange('centerImage', relativePath);
                }, images: uxPanelImages, title: "Select the UX panel art", isImageUsed: isUxPanelImageUsed }), _jsx("input", { type: "file", ref: fileInputRef, accept: ".csv", onChange: handleFileSelect, style: { display: 'none' } })] }));
};
